import { useState, useRef, useEffect } from "react";
import { t } from "i18next";
import { InferType, object, string } from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { tv } from "tailwind-variants";

import { getAppConfig } from "@/utils/appManager";
import { usePostSignup } from "@/requests/hooks/user";
import { useTracking } from "@/requests/hooks/app";
import formatError from "@/utils/formatError";
import { NAVIGATE_FORWARDS } from "@/utils/const";

import { Input } from "@/components/Input";
import { PasswordSwitcherButton } from "@/components/Button";
import { Caption } from "@/components/Typography";
import Checkbox from "@/components/Checkbox";
import { Button } from "@/components/Button";
import { Form } from "@/components/Form";

const styles = tv({
  slots: {
    base: "flex flex-col gap-20",
    buttonContainer: "min-w flex w-full min-w-fit justify-center",
    linkContainer: "mt-30 flex max-w-full items-center justify-center",
    termsAndConditions: "mt-20 text-left",
    passwordHelper: "my-5 flex flex-row items-center gap-5",
  },
});

type IProps = {
  onboardingFlow: IOnboardingFlows;
  handleIntegrationInstallationError?: () => void;
};

const SignUpForm = ({
  onboardingFlow,
  handleIntegrationInstallationError,
}: IProps) => {
  const formSchema = object({
    email: string()
      .email(t("common.signup.form.email.errors.workEmail"))
      .required(t("common.signup.form.email.errors.empty")),
    firstName: string().required(t("common.signup.form.firstName.empty")),
    lastName: string().required(t("common.signup.form.lastName.empty")),
    password: string()
      .required(t("common.signup.form.password.errors.empty"))
      .min(10, t("common.signup.form.password.errors.requirements")),
  });
  type ISignUpForm = InferType<typeof formSchema>;

  const {
    base,
    buttonContainer,
    linkContainer,
    termsAndConditions,
    passwordHelper,
  } = styles();

  const passwordRef = useRef<HTMLInputElement | null>(null);
  const [showPasswordInput, setShowPasswordInput] = useState<boolean>(false);
  const [isMarketingConsentChecked, setIsMarketingConsentChecked] =
    useState<boolean>(false);
  const [showCheckEmailAlert, setShowCheckEmailAlert] =
    useState<boolean>(false);

  const {
    mutate: postSignup,
    isPending: isSignupPending,
    error: signUpError,
    isSuccess: isSignupSuccess,
    data: signUpData,
  } = usePostSignup();

  const navigate = useNavigate();
  const { mutate: sendTracking } = useTracking();

  const {
    getValues,
    register,
    handleSubmit,
    setError,
    watch,
    trigger,
    formState: { errors, isSubmitted, isValid },
  } = useForm<ISignUpForm>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      password: "",
    },
  });

  const toggleMarketingConsentCheckbox = () =>
    setIsMarketingConsentChecked(isMarketingChecked => !isMarketingChecked);

  const toggleInputType = () =>
    setShowPasswordInput(setShowPassword => !setShowPassword);

  const isSubmitButtonDisabled = (): boolean =>
    isSubmitted &&
    (isSignupPending || !isValid || Object.keys(errors).length > 0);

  const validateAndSubmitForm = ({
    email,
    password,
    firstName,
    lastName,
  }: ISignUpForm) => {
    postSignup({
      data: {
        email,
        password,
        firstName,
        lastName,
        onboardingFlow,
        marketingConsent: isMarketingConsentChecked,
      },
      integration:
        onboardingFlow === "MARKETING" ? "?integration=SHOPIFY" : undefined,
    });
    sendTracking({
      eventName: "onboarding.signup.submit",
      additionalProperties: {
        source: onboardingFlow,
      },
    });
  };

  useEffect(() => {
    sendTracking({
      eventName: "onboarding.start",
      additionalProperties: {
        onboarding_flow: onboardingFlow,
      },
    });

    if (onboardingFlow === "MARKETING") {
      sendTracking({
        eventName: "signup.start",
        additionalProperties: {
          source: "shopify app store",
        },
      });
    }
  }, []);

  useEffect(() => {
    if (isSignupSuccess && !!signUpData.verificationToken) {
      localStorage.setItem("verificationToken", signUpData.verificationToken);
      navigate("/auth/email-verify", {
        state: {
          direction: NAVIGATE_FORWARDS,
          email: getValues("email"),
        },
      });
    }
  }, [isSignupSuccess]);

  useEffect(() => {
    if (!signUpError) return;

    const { codes, errors: coreApiErrors } = formatError(signUpError);

    if (codes?.includes("WORK_EMAIL_REQUIRED")) {
      setError("email", { message: coreApiErrors[0]?.message || "" });
    } else if (codes?.includes("INTEGRATION_INSTALLATION_NOT_FOUND")) {
      handleIntegrationInstallationError?.();
    } else if (codes?.includes("CHECK_EMAIL")) {
      setShowCheckEmailAlert(true);
    } else if (codes?.includes("Email")) {
      setError("email", { message: coreApiErrors[0]?.message || "" });
    }
  }, [signUpError]);

  useEffect(() => {
    if (isSubmitted) {
      trigger();
    }
  }, [
    watch("email"),
    watch("password"),
    watch("firstName"),
    watch("lastName"),
  ]);

  const renderFormInputs = () => {
    const { ref, ...rest } = register("password");

    const handleSharedRef = (e: HTMLInputElement | null) => {
      ref(e);
      passwordRef.current = e;
    };

    return (
      <div className={base()}>
        <div>
          <Input
            label={t("common.signup.form.email.label")}
            placeholder={t("common.signup.form.email.placeholder")}
            type="email"
            role="email"
            {...register("email")}
            helperText={errors?.email?.message}
            variant={!!errors?.email?.message ? "error" : undefined}
            helperTextTestId="email-helper-text"
          />
        </div>
        <div>
          <Input
            label={t("common.signup.form.firstName.label")}
            placeholder={t("common.signup.form.firstName.placeholder")}
            type="text"
            role="textbox"
            {...register("firstName")}
            helperText={errors?.firstName?.message}
            variant={!!errors?.firstName?.message ? "error" : undefined}
          />
        </div>
        <div>
          <Input
            label={t("common.signup.form.lastName.label")}
            placeholder={t("common.signup.form.lastName.placeholder")}
            type="text"
            role="textbox"
            {...register("lastName")}
            helperText={errors?.lastName?.message}
            variant={!!errors?.lastName?.message ? "error" : undefined}
          />
        </div>
        <div>
          <Input
            label={t("common.signup.form.password.label")}
            placeholder={t("common.signup.form.password.placeholder")}
            type={showPasswordInput ? "text" : "password"}
            ref={handleSharedRef}
            {...rest}
            role="textbox"
            helperText={errors?.password?.message}
            variant={!!errors?.password?.message ? "error" : undefined}
            trailingSlot={renderPasswordSwitcher()}
          />
          {renderPasswordHelper()}
        </div>
      </div>
    );
  };

  const renderFormCheckboxes = () => (
    <div className="my-20">
      <Checkbox
        label={t("common.signup.form.checkboxes.marketingConsent")}
        id="dataships-marketing-consent"
        name="dataships-marketing-consent"
        checked={isMarketingConsentChecked}
        onChange={toggleMarketingConsentCheckbox}
      />
    </div>
  );

  const renderFormButtons = () => (
    <>
      <div className={buttonContainer()}>
        <Button
          trackingId={{
            eventName: "onboarding.signup.submit",
            additionalProperties: {
              onboarding_flow: onboardingFlow,
            },
          }}
          variant="gradient"
          disabled={isSubmitButtonDisabled()}
          loading={isSignupPending}
          width="full"
          type="submit"
          testId="signup-submit">
          {t("common.signup.form.buttons.cta")}
        </Button>
      </div>
      {renderTermsAndConditions()}
      <div className={linkContainer()}>
        <Button
          as="link"
          trackingId={{
            eventName: "onboarding.signup.existing_user",
            additionalProperties: {
              onboarding_flow: onboardingFlow,
            },
          }}
          variant="link"
          iconPosition="end"
          icon={faCaretRight}
          iconClassName="text-primary-dark"
          to="/auth/login">
          {t("common.signup.form.buttons.secondary")}
        </Button>
      </div>
    </>
  );

  const renderTermsAndConditions = () => {
    const { website } = getAppConfig();
    return (
      <div className={termsAndConditions()}>
        <Trans as="span" i18nKey="common.signup.form.buttons.agree">
          One
          <Button
            as="a"
            href={website + "/terms-of-business"}
            variant="link"
            width="unset"
            extraClassNames="inline-flex">
            two
          </Button>
          Three
          <Button
            as="a"
            href={website + "/privacy-centre"}
            variant="link"
            width="unset"
            extraClassNames="inline-flex">
            Four
          </Button>
          .
        </Trans>
      </div>
    );
  };

  const renderPasswordHelper = () => {
    if (!errors?.password?.message)
      return (
        <div className={passwordHelper()}>
          <FontAwesomeIcon
            icon={faInfoCircle}
            className="h-15 w-15 text-secondary"
          />
          <Caption>{t("common.signup.form.password.helper")}</Caption>
        </div>
      );

    return <></>;
  };

  const renderPasswordSwitcher = () => (
    <PasswordSwitcherButton
      showPassword={showPasswordInput}
      onClick={toggleInputType}
    />
  );

  return (
    <>
      {showCheckEmailAlert && (
        <div className="bg-red">{t("common.signup.form.emailError")}</div>
      )}
      <Form className="mt-30" onSubmit={handleSubmit(validateAndSubmitForm)}>
        {renderFormInputs()}
        {renderFormCheckboxes()}
        {renderFormButtons()}
      </Form>
    </>
  );
};

export default SignUpForm;
